const instances = new WeakMap();

function hasText(value) {
  return typeof value === "string" && value.trim() !== "";
}

function createTrackingItem(template, item) {
  const article = template.content.firstElementChild.cloneNode(true);

  article.querySelectorAll("[data-tracking-field]").forEach((element) => {
    element.textContent = item[element.dataset.trackingField] ?? "";
  });

  article.querySelectorAll("[data-tracking-optional]").forEach((element) => {
    element.hidden = !hasText(item[element.dataset.trackingOptional]);
  });

  article.querySelector(".tracking-olva__details").hidden = ![
    item.observacion,
    item.motivo,
    item.observacion3,
  ].some(hasText);

  return article;
}

function getTrackingItems(result) {
  const items = result?.data?.tracking_info;

  return Array.isArray(items)
    ? items.filter(
        (item) =>
          item !== null && typeof item === "object" && !Array.isArray(item),
      )
    : [];
}

function init(root) {
  if (!root || instances.has(root)) return;

  // Component classes locate the form and output; data attributes describe states and template fields.
  const form = root.querySelector(".tracking-olva__form");
  const input = root.querySelector(".tracking-olva__form-input");
  const submitButton = root.querySelector(".tracking-olva__form-button");
  const timeline = root.querySelector(".tracking-olva__timeline");
  const template = root.querySelector("[data-tracking-item-template]");
  const mainCode = root.querySelector(".tracking-olva__tracking-number");
  const states = root.querySelectorAll("[data-tracking-state]");
  const shipmentFields = root.querySelectorAll(
    "[data-tracking-shipment-field]",
  );

  if (!form || !input || !submitButton || !timeline || !template) return;

  let activeRequest = null;

  function updateShipment(data = {}) {
    shipmentFields.forEach((element) => {
      const value = data[element.dataset.trackingShipmentField];
      element.textContent = hasText(value) ? value.trim() : "-";
    });
  }

  function setState(state) {
    states.forEach((element) => {
      element.hidden = element.dataset.trackingState !== state;
    });
    submitButton.disabled = state === "loading";
    form.setAttribute("aria-busy", String(state === "loading"));
  }

  async function fetchTrackingData(trackingCode) {
    activeRequest?.abort();
    const request = new AbortController();
    activeRequest = request;

    setState("loading");
    timeline.replaceChildren();
    updateShipment();

    if (mainCode) mainCode.textContent = trackingCode;

    try {
      const response = await fetch(
        `https://api-octo.fenrir.pe/api/shopify/tracking-olva/${encodeURIComponent(trackingCode)}`,
        { signal: request.signal },
      );
      if (!response.ok) throw new Error("Tracking request failed");

      const result = await response.json();
      // Also check identity: a response may finish after cancellation or a new search.
      if (activeRequest !== request || request.signal.aborted) return;

      const items = getTrackingItems(result);
      if (items.length === 0) {
        setState("empty");
        return;
      }

      const fragment = document.createDocumentFragment();
      items
        .slice()
        .reverse()
        .forEach((item) => {
          fragment.appendChild(createTrackingItem(template, item));
        });
      timeline.replaceChildren(fragment);
      updateShipment(result.data);
      setState("success");
    } catch {
      if (activeRequest === request && !request.signal.aborted)
        setState("error");
    } finally {
      if (activeRequest === request) activeRequest = null;
    }
  }

  function onSubmit(event) {
    event.preventDefault();
    const trackingCode = input.value.trim();
    if (!trackingCode) {
      input.focus();
      return;
    }
    fetchTrackingData(trackingCode);
  }

  instances.set(root, () => {
    activeRequest?.abort();
    activeRequest = null;
    form.removeEventListener("submit", onSubmit);
    setState("idle");
    instances.delete(root);
  });

  form.addEventListener("submit", onSubmit);
  setState("idle");
}

function findRoot(scope) {
  return scope.matches?.(".tracking-olva")
    ? scope
    : scope.querySelector(".tracking-olva");
}

// Module scripts execute once, including when the section markup references this asset again.
if (document.readyState === "loading") {
  document.addEventListener(
    "DOMContentLoaded",
    () => init(findRoot(document)),
    { once: true },
  );
} else {
  init(findRoot(document));
}

document.addEventListener("shopify:section:load", (event) =>
  init(findRoot(event.target)),
);
document.addEventListener("shopify:section:unload", (event) => {
  const root = findRoot(event.target);
  if (root) instances.get(root)?.();
});
